import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useAppContext } from '../AppContext';
import { Capacitor } from '@capacitor/core';
import { IapService } from '../services/IapService';
const ThemePurchaseModal = ({ onClose, handleBuyTheme, onApprove, themeId, themeName, price, previewClass }) => {
    const { LucideIcons, setMessage } = useAppContext();
    const [error, setError] = useState(null);
    const [isPurchasingNative, setIsPurchasingNative] = useState(false);
    const isNative = Capacitor.getPlatform() !== 'web';

    const handleNativeThemePurchase = async () => {
        setIsPurchasingNative(true);
        try {
            const offerings = await IapService.getOfferings();
            if (offerings && offerings.current && offerings.current.availablePackages) {
                // Find consumable package matching theme price or identifier
                const pkg = offerings.current.availablePackages.find(p => p.identifier === 'theme_' + themeId) || offerings.current.availablePackages[0];
                if (pkg) {
                    await IapService.purchasePackage(pkg);
                    setMessage('Theme purchased successfully!');
                    handleBuyTheme(themeId, price); // Notify backend/state
                    onClose();
                } else {
                    setError('Theme package not found in store.');
                }
            } else {
                setError('No store packages available.');
            }
        } catch (error) {
            console.error("Native theme purchase failed", error);
            setError("Failed to purchase via App Store");
        } finally {
            setIsPurchasingNative(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className={`p-6 rounded-lg shadow-xl max-w-md w-full text-white relative border border-gray-600 animate-fadeIn ${previewClass ? previewClass : 'bg-gray-900'}`}>
                {/* Overlay to ensure text readability if theme is very bright, though most themes are dark */}
                <div className="absolute inset-0 bg-black/40 rounded-lg pointer-events-none"></div>
                
                <div className="relative z-10">
                    <button 
                        onClick={onClose} 
                        className="absolute -top-2 -right-2 text-gray-300 hover:text-white transition-colors bg-black/20 rounded-full p-1"
                    >
                        <LucideIcons.X size={24} />
                    </button>

                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-white mb-2 shadow-sm">
                            Unlock {themeName}
                        </h2>
                        <p className="text-gray-200">
                            Enhance your profile with this premium theme for just <span className="font-bold text-green-400 text-lg">${price}</span>.
                        </p>
                    </div>

                    <div className="mb-6">
                        <div className={`w-full h-32 rounded-lg mb-4 border-2 border-white/20 flex items-center justify-center shadow-inner ${previewClass}`}>
                            <span className="text-white font-bold font-playfair text-xl drop-shadow-md">Theme Preview</span>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 rounded-md mb-4 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="w-full">
                        {!isNative ? (
                            <PayPalScriptProvider options={{ 
                                "client-id": "AV_SaODc_D66O66nQyMz5GN98n9F0ZXhRHkS-A1eJpN1Wdq6f9sc_bkASy_0qJNPtb9KviK5YwqLgmqs",
                                components: "buttons",
                                currency: "USD"
                            }}>
                                <PayPalButtons 
                                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                                    createOrder={(data, actions) => handleBuyTheme(themeId, price)}
                                    onApprove={(data, actions) => onApprove(data, actions)}
                                    onError={(err) => {
                                        console.error("PayPal Error:", err);
                                        setError("Payment failed. Please try again.");
                                    }}
                                />
                            </PayPalScriptProvider>
                        ) : (
                            <button 
                                onClick={handleNativeThemePurchase}
                                disabled={isPurchasingNative}
                                className="w-full bg-green-500 hover:bg-green-600 font-bold py-3 text-lg rounded-md transition"
                            >
                                {isPurchasingNative ? 'Processing...' : `Buy with ${Capacitor.getPlatform() === 'ios' ? 'Apple' : 'Google'} Pay`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThemePurchaseModal;